import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import { IconButton } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { RootState } from '~/store/config';
import * as actions from '~/store/actions';
import { IRules, ICallTaggingInfo } from '~/store/reducers/afterCallWork';
import { formatInternationalE164, electron } from '~/helpers';
import * as actionTypes from '~/store/actions/actionTypes';
import * as NuacomComponent from '~/components';
import { Dialog, DialogTitle, DialogContent, LinearProgress } from './styles';

interface ITag {
  assigned: number;
  tag_color: string;
  tag_id: string;
  tag_name: string;
}

interface INote {
  account_id: string;
  call_id: string;
  id: string;
  note: string;
  note_time: string;
  user_id: number;
  user_name: string;
}

export default function MandatoryCallTagging() {
  const dispatch = useDispatch();

  const [callTaggingInfo, setCallTaggingInfo] = useState<ICallTaggingInfo>();
  const [countdown, setCountdown] = useState(-1);
  const [progress, setProgress] = useState(0);

  const allTags = useSelector((state: RootStateOrAny) => state.tags.data);
  const allNotes = useSelector((state: RootStateOrAny) => state.draft.notes);
  const { mandatoryCallTagging, busyState } = useSelector(
    (state: RootState) => state.afterCallWork
  );
  const isBusy = useSelector((state: RootState) => state.system.isBusy);
  const phone = useSelector((state: RootState) => state.phone);

  const getMinNumberOfRules = (rules: IRules) => {
    let count = 0;
    if (rules.notes) count += 1;
    if (rules.tags) count += rules.tags;

    return count;
  };

  // Display the "X" button allowing the user's to close the dialog
  const allowCloseDialog = useCallback(
    (callTagInfo: ICallTaggingInfo) => {
      setCountdown(-1);
      dispatch(actions.expireMandatoryCallTaggingDialog(callTagInfo));
    },
    [setCountdown, dispatch]
  );

  useEffect(() => {
    if (mandatoryCallTagging.length) {
      const timerInMinutes = mandatoryCallTagging[0].rules.timer;

      setCallTaggingInfo(mandatoryCallTagging[0]);

      const timerInSeconds = timerInMinutes * 60;
      setCountdown(timerInSeconds);
    }
  }, [mandatoryCallTagging, setCallTaggingInfo]);

  useEffect(() => {
    if (callTaggingInfo) {
      const { callId, rules } = callTaggingInfo;

      const assignedTags = allTags[callId]?.filter((e: ITag) => e.assigned);

      const addedNotes = allNotes?.filter((e: INote) => e.call_id === callId);

      if (assignedTags && addedNotes) {
        const numberOfRulesRequired = getMinNumberOfRules(rules);
        const numberOfRulesFullfield = assignedTags.length + addedNotes.length;
        const atualProgress =
          (100 / numberOfRulesRequired) * numberOfRulesFullfield;
        setProgress(atualProgress);
      }

      // Check if it tags added match the minimum amount of tags required by after call rules
      if (assignedTags?.length >= rules.tags) {
        if (rules.notes && addedNotes?.length) {
          allowCloseDialog(callTaggingInfo);
        } else if (!rules.notes) {
          allowCloseDialog(callTaggingInfo);
        }
      }
    }
  }, [
    callTaggingInfo,
    allTags,
    allNotes,
    setCountdown,
    allowCloseDialog,
    setProgress,
    dispatch,
  ]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => setCountdown(countdown - 1), 1000);
      return () => clearInterval(timer);
    }

    if (countdown === 0) {
      setCountdown(-1);

      if (callTaggingInfo) {
        dispatch(actions.expireMandatoryCallTaggingDialog(callTaggingInfo));
      }
    }

    return () => {};
  }, [countdown, callTaggingInfo, dispatch]);

  useEffect(() => {
    if (callTaggingInfo?.callId) {
      const { callId, rules } = callTaggingInfo;
      electron.log(
        `NUACOM-LOG: [TAGS] - Mandatory Call Tagging requires at least ${rules.tags} tags and ${rules.notes} notes caracters within ${rules.timer} minute(s) for callId: ${callId}`,
        callTaggingInfo
      );
    }
    // runs only once, on first render / mount
  }, [callTaggingInfo]);

  /**
   * Method is called when dialog "X" is clicked by user interaction
   */
  const handleClose = async () => {
    electron.log(
      `NUACOM-LOG: [TAGS] - Mandatory Call Tagging dialog closed by user for callId: ${callTaggingInfo?.callId}`
    );

    setCallTaggingInfo(undefined);

    // TODO: Is it possible to include calls.length?
    if (isBusy && busyState) {
      dispatch(actions.setIsBusy(false));
      dispatch(actions.statusSystem(actionTypes.STATUS.AVAILABLE));
      await phone.ua?.register();
      dispatch({
        type: actionTypes.MANDATORY_BUSY_STATE_CHANGE,
        payload: false,
      });
    }
  };

  return (
    <Dialog
      open={!!callTaggingInfo?.callId}
      aria-labelledby="mandatory-call-tagging-dialog">
      <LinearProgress variant="determinat" style={{ width: `${progress}%` }} />
      <DialogTitle disableTypography>
        <div className="details">
          <span className="header-name">Mandatory Call Tagging</span>
          <span className="contact-name">
            {formatInternationalE164(callTaggingInfo?.contactName)}
          </span>
          <span className="number-from">
            {formatInternationalE164(callTaggingInfo?.numberFrom)}
          </span>
        </div>

        <div className="actions">
          {countdown > 0 ? (
            <span className="countdown">{countdown}s</span>
          ) : (
            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </DialogTitle>
      <DialogContent>
        {callTaggingInfo?.callId && (
          <>
            <NuacomComponent.SendNotes
              callId={callTaggingInfo.callId}
              minWords={callTaggingInfo.rules.notes}
              open
              onClose={() => false}
            />

            <NuacomComponent.AddTags
              open
              callId={callTaggingInfo.callId}
              onClose={() => false}
              callInfo={callTaggingInfo}
            />

            <NuacomComponent.ShowTags callId={callTaggingInfo.callId} />

            <NuacomComponent.ShowNotes callId={callTaggingInfo.callId} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
